import PatientAppointmentUpdate from "issara-sdk/apis/PatientAppointmentUpdate_apps_QUE";
import AppointmentFinish from "issara-sdk/apis/AppointmentFinish_apps_APP";
import PackageDetail from "issara-sdk/apis/PackageDetail_core";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";
import {
  GetAppointmentDetail,
  GetPatientAppointmentList,
} from "react-lib/apps/Scheduling/SchedulingInterface";
import { adToBe, beStringToAdString, beToAd } from "react-lib/utils/dateUtils";
import moment from "moment";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  SEARCH: "SEARCH",
  UPDATE_APPOINTMENT: "UPDATE_APPOINTMENT",
  SELECTED_APPOINTMENT_DETAIL: "SELECTED_APPOINTMENT_DETAIL",
  ASSIGN_APPOINTMENT_TO_BLOCK: "ASSIGN_APPOINTMENT_TO_BLOCK",
  CONFIRM_APPOINTMENT: "CONFIRM_APPOINTMENT",
  SET_APPOINTMENT_FINISH: "SET_APPOINTMENT_FINISH",
  CONFIRM_APPOINTMENT_FINISH: "CONFIRM_APPOINTMENT_FINISH",
  GET_PACKAGE_DETAIL: "GET_PACKAGE_DETAIL",
} as const;

export const SEQUENCE_NAME = "AppointmentListMEH" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  AppointmentListMEHSequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    filterAppointmentList: FilterAppointmentType;
    appointmentList: Record<any, string>[];
    appointmentDetail: AppointmentDetailType;
    appointmentFinish: AppointmentFinishType[];
    appointmentListSummaryPage: {
      activePage: number | null;
      total: number | null;
      count?: number;
    };
    packageDetail: Record<any, string> | null;
  }> | null;
  availableSlots?: any[];
  selectedBlock?: any;
  userTokenize?: Record<string, any>;
}>;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "searchedItemListWithKey"
>;

export type appointmentStatusType = "ยืนยันนัดหมาย" | "ยกเลิก" | "รอยืนยัน";
export type serviceStatusType = "รับบริการแล้ว" | "ยังไม่ได้รับบริการ";

export type AppointmentDetailType = {
  intervalTimeSelected?: number;
  id?: number;
  patient?: string;
  tel_text?: string;
  doctor_full_name?: string;
  appointment_status_text?: string;
  order_note?: string;
  service_status_text?: string;
  service_date_text?: string;
  provider?: string;
  display_info?: string;
  estimated_duration?: string;
  detail?: string;
  extra?: any;
  division?: number;
  division_name?: string;
  dsb_full?: boolean;
  manualTime?: boolean;
  minHour?: string;
  maxHour?: string;
  estimatedAt?: string;
  date?: string;
  status_note?: string;
  edit_user_employee_name?: string;
  estimated_at?: string;
  patient_id?: number;
  content_id?: number;
  statusNote?: string;
  type?: string;
  division_service_block?: number;
};

export type FilterAppointmentType = {
  id?: string;
  patientID?: string;
  startDate?: string;
  endDate?: string;
  providerID?: string | number;
  appointmentStatus?: string;
  orderStatus?: string;
  noDivisionServiceBlock?: boolean;
};

export type AppointmentFinishType = {
  checked?: boolean;
  id?: string;
  patientName?: string;
  estimatedAt?: string;
  doctorName?: string;
};

export type Event =
  | { message: "GetMasterData"; params: Record<string, any> }
  | { message: "RunSequence"; params: Record<string, any> };

export type Data = { division?: number };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.SEARCH]: { activePage?: number };
  [ACTIONS.SELECTED_APPOINTMENT_DETAIL]: { data?: Record<string, any> };
  [ACTIONS.ASSIGN_APPOINTMENT_TO_BLOCK]: {
    data?: Record<string, any>;
  };
  [ACTIONS.CONFIRM_APPOINTMENT]: {
    data?: Record<string, any>;
    callback?: (status: string) => void;
  };
  [ACTIONS.SET_APPOINTMENT_FINISH]: { data?: Record<string, any> };
  [ACTIONS.CONFIRM_APPOINTMENT_FINISH]: { callback?: (status: []) => void };
  [ACTIONS.UPDATE_APPOINTMENT]: {
    callback?: (status: boolean) => void;
  };
  [ACTIONS.GET_PACKAGE_DETAIL]: { packageId: number };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  AppointmentListMEHSequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

const Masters = [
  ["doctor", {}],
  ["prenameTh", {}],
] as const;

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  await controller.handleEvent({
    message: "GetMasterData",
    params: {
      masters: Masters,
    },
  });

  const toDate = moment();
  const endDate = toDate.clone().add(7, "days");

  controller.setState(
    {
      AppointmentListMEHSequence: {
        ...state.AppointmentListMEHSequence,
        sequenceIndex: "Action",
        filterAppointmentList: {
          startDate: adToBe(toDate.format("DD/MM/YYYY")) || "",
          endDate: adToBe(endDate.format("DD/MM/YYYY")) || "",
          providerID: "ทั้งหมด",
          appointmentStatus: "ทั้งหมด",
          orderStatus: "ทั้งหมด",
          id: "",
          patientID: "",
          noDivisionServiceBlock: false,
        },
        appointmentListSummaryPage: {
          activePage: 0,
          total: 0,
        },
      },
    },
    () => {
      Action(controller, { ...params, action: ACTIONS.SEARCH });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SELECTED_APPOINTMENT_DETAIL]: HandleSelectedAppointmentDetail,
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.ASSIGN_APPOINTMENT_TO_BLOCK]: HandleSetAppointmentToBlock,
    [ACTIONS.CONFIRM_APPOINTMENT]: HandleConfirmAppointment,
    [ACTIONS.SET_APPOINTMENT_FINISH]: HandleAppointmentFinish,
    [ACTIONS.CONFIRM_APPOINTMENT_FINISH]: HandleConfirmAppointmentFinish,
    [ACTIONS.UPDATE_APPOINTMENT]: HandleUpdateAppointment,
    [ACTIONS.GET_PACKAGE_DETAIL]: HandleGetPackageDetail,
  };
  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  const filterData = { ...state.AppointmentListMEHSequence?.filterAppointmentList };

  if (filterData?.providerID === "ทั้งหมด") {
    delete filterData?.providerID;
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
  });

  const parseStatus = (value = "", allValues: string[]): string[] =>
    !value ? [] : value === "ทั้งหมด" ? allValues : value.split(",");

  const appointmentStatuses = parseStatus(filterData?.appointmentStatus, allAppointmentStatus);

  const orderStatuses = parseStatus(filterData?.orderStatus, allOrderStatus);

  const statusArray =
    appointmentStatuses.length && orderStatuses.length
      ? appointmentStatuses.filter((s) => orderStatuses.includes(s))
      : [...appointmentStatuses, ...orderStatuses];

  const status = statusArray.length ? [...new Set(statusArray)].join(",") : "-1";

  let paramsData = {};

  if (filterData?.noDivisionServiceBlock) {
    paramsData = {
      id: filterData?.id,
      patient_id: filterData?.patientID,
      states: 3,
      no_division_service_block: filterData?.noDivisionServiceBlock,
      ...(filterData?.providerID !== "ทั้งหมด" && {
        provider_id: filterData?.providerID,
      }),
      exclude_status: "5,6",
      limit: 10,
      offset: ((params.activePage || 1) - 1) * 10,
    };
  } else {
    paramsData = {
      division_id: controller.data.division,
      id: filterData?.id,
      patient_id: filterData?.patientID,
      ...(filterData?.startDate && {
        start_datetime: getISODate(filterData?.startDate || "", "start"),
      }),
      ...(filterData?.endDate && {
        end_datetime: getISODate(filterData?.endDate || "", "end"),
      }),
      ...(filterData?.providerID !== "ทั้งหมด" && {
        provider_id: filterData?.providerID,
      }),
      status,
      limit: 10,
      offset: ((params.activePage || 1) - 1) * 10,
    };
  }

  const patientAppointmentList = await GetPatientAppointmentList(controller as any, {
    paramsData,
    isPatientAppointmentV2: true,
  });

  const appointmentList = (patientAppointmentList?.items || []).map((item: any) => {
    return {
      ...item,
      appointment_status_text:
        appointmentStatusOptions.find((i: any) => i.value.includes(item.status.toString()))?.text ||
        "",
      service_status_text: serviceStatus(item),
    };
  });

  if (patientAppointmentList) {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS },
      AppointmentListMEHSequence: {
        ...state.AppointmentListMEHSequence,
        appointmentList: appointmentList || [],
        appointmentListSummaryPage: {
          ...state.AppointmentListMEHSequence?.appointmentListSummaryPage,
          count: patientAppointmentList.total,
          ...(params?.activePage
            ? {
                activePage: params?.activePage,
                total: Math.ceil(patientAppointmentList.total / 10),
              }
            : {
                activePage: 1,
                total: Math.ceil(patientAppointmentList.total / 10),
              }),
        },
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.ERROR },
    });
  }
};

const HandleSelectedAppointmentDetail: Handler<Params<"SELECTED_APPOINTMENT_DETAIL">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  // params.data?.startDate || params.data?.endDate มาจาก HandleSetAppointmentToBlock
  const startIso = params.data?.startDate || params.data?.display_info?.start_datetime_iso;
  const endIso = params.data?.endDate || params.data?.display_info?.end_datetime_iso;

  const minHour = startIso?.split("T")[1]?.substring(0, 5) ?? "";
  const maxHour = endIso?.split("T")[1]?.substring(0, 5) ?? "";

  const iso = params.data?.startDate || params.data?.estimated_at_iso;

  const date = adToBe(moment(iso).format("DD/MM/YYYY")) ?? "";
  const estimatedAt = moment(iso).format("HH:mm") ?? "";

  GetAppointmentDetail(controller as any, {
    selectedAppointment: params.data?.id,
    doctor_dsb_id: params.data?.division_service_block,
    selectedBlock: state.selectedBlock,
    card: SEQUENCE_NAME,
  });

  controller.setState({
    userTokenize: {},
    AppointmentListMEHSequence: {
      ...state.AppointmentListMEHSequence,
      appointmentDetail: {
        ...params.data,
        manualTime: false,
        estimatedAt,
        minHour,
        maxHour,
        date,
      },
    },
  });
};

const HandleSetAppointmentToBlock: Handler<Params<"ASSIGN_APPOINTMENT_TO_BLOCK">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  let datetime = `${adToBe(params.data?.dsb?.date, "YYYY-MM-DD")} ${
    params.data?.doctor_start_time || ""
  }-${params.data?.doctor_end_time || ""}`;

  controller.setState({
    selectedBlock: params,
  });

  Action(controller, {
    data: {
      ...state.AppointmentListMEHSequence?.appointmentDetail,
      division_service_block: params.data?.doctor_dsb_id,
      detail: `${params.data?.division_name || ""} (${params.data?.doctor_provider_name || ""} ${
        datetime || ""
      })`,
      doctor_full_name: params.data?.doctor_provider_name || params.data?.dsb?.doctor_provider_name,
      division: params.data?.division_id || params.data?.dsb?.division_id,
      division_name: params.data?.division_name || params.data?.dsb?.division_name,
      dsb_full: false,
      extra: {
        chair:
          params.data?.chair || state.AppointmentListMEHSequence?.appointmentDetail?.extra?.chair,
        appointmentFor: state.AppointmentListMEHSequence?.appointmentDetail?.extra?.appointmentFor,
        jobToDo: state.AppointmentListMEHSequence?.appointmentDetail?.extra?.jobToDo,
      },
      ...(params.data?.division_service_block && {
        division_service_block: params.data?.division_service_block,
      }),
      startDate: moment(params.data?.startTime).format(),
      endDate: moment(params.data?.endTime).format(),
    },
    sequence: "AppointmentListMEH",
    action: "SELECTED_APPOINTMENT_DETAIL",
  });
};

const HandleConfirmAppointment: Handler<Params<"CONFIRM_APPOINTMENT">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.CONFIRM_APPOINTMENT]: BLC_STATUS.LOADING,
    },
  });

  const isConfirm = params.data?.type === "ได้รับบริการแล้ว";

  const apiCall = isConfirm
    ? AppointmentFinish.post({
        data: {
          items: [
            {
              checked: true,
              id: state.AppointmentListMEHSequence?.appointmentDetail?.content_id,
            },
          ],
        },
        apiToken: controller.apiToken,
        extra: { division: controller.data.division },
      })
    : PatientAppointmentUpdate.update({
        pk: state.AppointmentListMEHSequence?.appointmentDetail?.id,
        data: {
          patient: state.AppointmentListMEHSequence?.appointmentDetail?.patient_id,
          status:
            params.data?.type === "ยืนยันนัดหมาย"
              ? 2
              : params.data?.type === "เลื่อนแบบไม่ระบุวัน"
              ? 4
              : params.data?.type === "ผู้รับบริการขอยกเลิก"
              ? 5
              : 6,
          ...(params.data?.type === "เลื่อนแบบไม่ระบุวัน" && { division_service_block: null }),
          division: state.AppointmentListMEHSequence?.appointmentDetail?.division,
          ...(state.AppointmentListMEHSequence?.appointmentDetail?.statusNote && {
            status_note: state.AppointmentListMEHSequence?.appointmentDetail?.statusNote,
          }),
        },
        apiToken: controller.apiToken,
      });

  const [res, err] = await apiCall;

  if (res) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.CONFIRM_APPOINTMENT]: BLC_STATUS.SUCCESS,
      },
    });

    params.callback?.("");
    Action(controller, { sequence: "AppointmentListMEH", action: "SEARCH" });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.CONFIRM_APPOINTMENT]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [SEQUENCE_NAME]: err,
      },
    });
  }
};

const HandleAppointmentFinish: Handler<Params<"SET_APPOINTMENT_FINISH">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SET_APPOINTMENT_FINISH]: BLC_STATUS.LOADING,
    },
  });

  const appointmentData = (state.AppointmentListMEHSequence?.appointmentList || [])
    ?.filter((item) => {
      return (params.data || []).includes(item.id);
    })
    .map((item) => {
      return {
        checked: true,
        id: item?.content_id,
        patientName: item?.patient,
        estimatedAt: item?.estimated_at,
        doctorName: item?.doctor_full_name,
      };
    });

  controller.setState({
    AppointmentListMEHSequence: {
      ...state.AppointmentListMEHSequence,
      appointmentFinish: appointmentData,
    },
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SET_APPOINTMENT_FINISH]: BLC_STATUS.SUCCESS,
    },
  });
};

const HandleConfirmAppointmentFinish: Handler<Params<"CONFIRM_APPOINTMENT_FINISH">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.CONFIRM_APPOINTMENT_FINISH]: BLC_STATUS.LOADING,
    },
  });

  const [appointmentFinishRes, appointmentFinishErr, appointmentFinishNet] =
    await AppointmentFinish.post({
      data: {
        items: state.AppointmentListMEHSequence?.appointmentFinish,
      },
      apiToken: controller.apiToken,
      extra: { division: controller.data.division },
    });

  if (appointmentFinishRes) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.CONFIRM_APPOINTMENT_FINISH]: BLC_STATUS.SUCCESS,
      },
      AppointmentListMEHSequence: {
        ...state.AppointmentListMEHSequence,
        appointmentFinish: [],
      },
    });

    params.callback?.([]);
    Action(controller, { sequence: "AppointmentListMEH", action: "SEARCH" });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.CONFIRM_APPOINTMENT_FINISH]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [SEQUENCE_NAME]: appointmentFinishErr,
      },
    });
  }
};

const GetBlockList = (state: any): any[] =>
  (state.MakeAppointmentMeHeySequence?.dsb || [])
    .flatMap((item: any) => item.providers || [])
    .map((item: any) => item.dsb)
    .filter((dsb: any) => dsb?.doctor_dsb_id);

// block ของ provider คนเดิมในวันนั้น มีหลายอันให้เอาอันที่ช่วงเวลาครอบเวลานัด
const FindProviderBlock = (blocks: any[], detail: any, date: string, time: string) => {
  const candidates = blocks.filter(
    (dsb: any) => dsb.date === date && dsb.doctor_provider_id === detail?.display_info?.provider
  );

  return (
    candidates.find((dsb: any) => time >= dsb.doctor_start_time && time <= dsb.doctor_end_time) ||
    candidates[0]
  )?.doctor_dsb_id;
};

const HandleUpdateAppointment: Handler<Params<"UPDATE_APPOINTMENT">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.UPDATE_APPOINTMENT]: BLC_STATUS.LOADING,
    },
  });

  const detail = state.AppointmentListMEHSequence?.appointmentDetail;

  const newDate = beToAd(detail?.date || "");
  const date = newDate?.format("YYYY-MM-DD") || "";

  // เทียบวันจาก block ที่ผูกอยู่จริงตอนนี้ (ถ้าเพิ่งเลือก slot จากแท็บ "เลือกวันเวลานัดหมาย" ค่านี้คือ slot ใหม่)
  const blocks = GetBlockList(state);
  const currentBlock = blocks.find(
    (dsb: any) => dsb.doctor_dsb_id === detail?.division_service_block
  );

  // ยังไม่มีตารางในมือให้เทียบ (state ว่าง) → คงค่าเดิมไว้ ดีกว่าเดาผิด
  const keepBlock = !date || !blocks.length || currentBlock?.date === date;

  const blockId = keepBlock
    ? detail?.division_service_block
    : FindProviderBlock(blocks, detail, date, detail?.estimatedAt || "");

  if (!keepBlock && !blockId) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.UPDATE_APPOINTMENT]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [SEQUENCE_NAME]: 'ไม่พบตารางของผู้ให้บริการในวันที่เลือก กรุณาเลือกจากแท็บ "เลือกวันเวลานัดหมาย"',
      },
    });

    return;
  }

  const [patientAppointmentRes, patientAppointmentErr, patientAppointmentNet] =
    await PatientAppointmentUpdate.update({
      pk: detail?.id,
      data: {
        date,
        patient: detail?.patient_id,
        division: detail?.division,
        estimated_at_iso: getISODate(detail?.date || "", detail?.estimatedAt),
        division_service_block: blockId,
      },
      apiToken: controller.apiToken,
    });

  if (patientAppointmentRes) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.UPDATE_APPOINTMENT]: BLC_STATUS.SUCCESS,
      },
      AppointmentListMEHSequence: {
        ...state.AppointmentListMEHSequence,
        appointmentDetail: {},
      },
    });

    params.callback?.(false);
    Action(controller, { sequence: "AppointmentListMEH", action: "SEARCH" });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.UPDATE_APPOINTMENT]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [SEQUENCE_NAME]: patientAppointmentErr,
      },
    });
  }
};

const HandleGetPackageDetail: Handler<Params<"GET_PACKAGE_DETAIL">> = async (
  controller,
  params
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.GET_PACKAGE_DETAIL]: BLC_STATUS.LOADING,
    },
  });

  const [result, error] = await PackageDetail.retrieve({
    apiToken: controller.apiToken,
    pk: params.packageId,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.GET_PACKAGE_DETAIL]: null,
    },
    AppointmentListMEHSequence: {
      ...state.AppointmentListMEHSequence,
      packageDetail: result,
    },
  });
};

const serviceStatus = (data: any) => {
  let appointmentDate = data?.display_info?.provider
    ? moment(data?.estimated_at_iso).format("YYYY/MM/DD")
    : moment(data?.display_info?.start_datetime_iso).format("YYYY/MM/DD");

  let serviceDate = moment(data?.updated).format("YYYY/MM/DD");

  // มารับบริการก่อนวันที่นัดหมาย || มารับบริการเลยวันที่นัดหมาย
  if (data?.status === 8) {
    return "รับบริการแล้ว";
  } else {
    return "ยังไม่ได้รับบริการ";
  }
};

const ISO_FORMAT = "YYYY-MM-DDTHH:mm:ssZZ";

const getISODate = (date: string, option?: string): string => {
  const newDate = beStringToAdString(date);
  const base = moment(newDate, "DD/MM/YYYY");

  const isTime = option !== undefined && /^\d{2}:\d{2}$/.test(option);

  if (isTime) {
    const [h, m] = option!.split(":");
    return base.hour(Number(h)).minute(Number(m)).second(0).utcOffset("+07:00").format(ISO_FORMAT);
  }

  if (option === "start") {
    return base.startOf("day").utcOffset("+07:00").format(ISO_FORMAT);
  }

  if (option === "end") {
    return base.endOf("day").utcOffset("+07:00").format(ISO_FORMAT);
  }

  return base.utcOffset("+07:00").format(ISO_FORMAT);
};

export const orderStatusOptions = [
  { key: "ยังไม่ได้รับบริการ", text: "ยังไม่ได้รับบริการ", value: "1,2,3,4,5,6,7" },
  { key: "รับบริการแล้ว", text: "รับบริการแล้ว", value: "8" },
];

export const appointmentStatusOptions = [
  { key: 1, text: "รอยืนยัน", value: "1" },
  { key: 2, text: "ยืนยันนัดหมาย", value: "2,8" },
  { key: 5, text: "ยกเลิกนัดหมาย", value: "5,6" },
  { key: 3, text: "ถูกเลื่อนนัด", value: "3" },
];

const allAppointmentStatus = ["1", "2", "3", "5", "6", "8"];
const allOrderStatus = ["1", "2", "3", "4", "5", "6", "7", "8"];
